import * as Sentry from '@sentry/react';
import {
  deleteAddress,
  getCreditTransations,
  getDinerNotices,
  getMembershipSaving as callGetMembershipSaving,
  getReferralCode as callGetReferralCode,
  getUser as callGetuser,
  getUserIntercomCredentials,
  login as postLogin,
  logoutUser,
  persistNewAddress,
  postCode,
  putAddress,
  putCode,
  putUser,
  refreshUserToken as postRefreshToken,
} from '~/api/userApi';
import { selectLocale } from '~/store/legacy/selector/appSelectors';
import { selectUser, selectUserServiceRegionId } from '~/store/legacy/selector/userSelectors';
import { getCookie } from '~/store/storage';
import { snakeToCamel } from '~/utils/object';
import { intercom } from '../../../third_party/Intercom';
import { mixpanel } from '../../../third_party/Mixpanel';
import { openAlert } from './alertActions.js';
import * as deliveryActions from './deliveryActions';
import { closeDialog } from './dialogActions.js';
import { SeverityLevel_error } from '~/constant.js';

const BASE = 'user :';
const refreshTokenCallbacks = [];

export const userActionTypes = {
  init: `${BASE} init`,

  loginStart: `${BASE} login : persist start`,
  loginSuccess: `${BASE} login : persist SUCCESS`,
  loginFailed: `${BASE} login : persist FAILED`,

  logout: `${BASE} logout`,

  refreshingTokenStart: `${BASE} refereshing token : persist start`,
  refreshTokenSuccess: `${BASE} refereshing token : persist SUCCESS`,
  refreshTokenFailed: `${BASE} refereshing token : persist FAILED`,

  getUserStart: `${BASE} fetch start`,
  getUserSuccess: `${BASE} fetch SUCCESS`,
  getUserFailed: `${BASE} fetch FAILED`,

  updateUserStart: `${BASE} persist start`,
  updateUserSuccess: `${BASE} persist SUCCESS`,
  updateUserFailed: `${BASE} persist FAILED`,

  updateUserSearchAddress: `${BASE} set search address`,

  selectUserAddress: `${BASE} choose address`,

  persistNewUserAddress_start: `${BASE} new address : persist start`,
  persistNewUserAddress_success: `${BASE} new address : persist SUCCESS`,
  persistNewUserAddress_failed: `${BASE} new address : persist FAILED`,

  updateUserAddressStart: `${BASE} existing address : persist start`,
  updateUserAddressSuccess: `${BASE} existing address : persist SUCCESS`,
  updateUserAddressFailed: `${BASE} existing address : persist FAILED`,

  deleteUserAddressStart: `${BASE} existing address : delete start`,
  deleteUserAddressSuccess: `${BASE} existing address : delete SUCCESS`,
  deleteUserAddressFailed: `${BASE} existing address : delete FAILED`,

  sendUserPhoneVerificationStart: `${BASE} send phone verification : start`,
  sendUserPhoneVerificationSuccess: `${BASE} send phone verification : SUCCESS`,
  sendUserPhoneVerificationFailed: `${BASE} send phone verification : FAILED`,

  verifyUserStart: `${BASE} verify : start`,
  verifyUserSuccess: `${BASE} verify : SUCCESS`,
  verifyUserFailed: `${BASE} verify : FAILED`,

  getCreditStart: `${BASE} credit : fetch start`,
  getCreditSuccess: `${BASE} credit : fetch SUCCESS`,
  getCreditFailed: `${BASE} credit : fetch FAILED`,

  getReferralCodeStart: `${BASE} referral code : fetch start`,
  getReferralCodeSuccess: `${BASE} referral code : fetch SUCCESS`,
  getReferralCodeFailed: `${BASE} referral code : fetch FAILED`,

  getUserNotificationStart: `${BASE} notification : fetch start`,
  getUserNotificationSuccess: `${BASE} notification : fetch SUCCESS`,
  getUserNotificationFailed: `${BASE} notification : fetch FAILED`,

  getMembershipSavingStart: `${BASE} memebership savings : fetch start`,
  getMembershipSavingSuccess: `${BASE} memebership savings : fetch SUCCESS`,
  getMembershipSavingFailed: `${BASE} memebership savings : fetch FAILED`,

  setUserServiceType: `${BASE} set serive type`,

  setUserAddressError: `${BASE} set address error`,
};

export const initUser = (user) => (dispatch) => {
  const {
    address,
    geoLocation,
    addressId,
    accessToken,
    tokenType,
    createdAt,
    serviceRegionId,
  } = user;
  dispatch({
    type: userActionTypes.init,
    address,
    geoLocation,
    addressId,
    accessToken,
    tokenType,
    createdAt,
    serviceRegionId,
  });
};

export const login = ({ email, password, provider, providerUid, providerToken }) => async (
  dispatch,
  getState,
  { intl }
) => {
  mixpanel.track({ name: 'sign-in : sign-in-submitted', properties: { method: provider } });
  const { formatMessage } = intl(getState());
  dispatch({
    type: userActionTypes.loginStart,
  });
  try {
    const { data } = await postLogin({
      email,
      password,
      provider,
      providerUid,
      providerToken,
      castleRequestToken: '',
    });
    const { access_token, refresh_token, token_type, created_at, expires_in = 1800 } = data;
    dispatch({
      type: userActionTypes.loginSuccess,
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      createdAt: created_at,
      expires_in,
      email,
    });
    dispatch(
      openAlert({
        variant: 'success',
        message: formatMessage({ id: 'alert.sign-in.login.success' }),
      })
    );
    mixpanel.track({ name: 'sign-in : sign-in-succeeded', properties: { method: provider } });
    await dispatch(getUser());
    const serviceRegionId = selectUserServiceRegionId(getState());
    dispatch(updateUserServiceRegion(serviceRegionId));
  } catch (exception) {
    Sentry.addBreadcrumb({
      category: 'User Login',
      message: 'User Login failed',
      level: SeverityLevel_error,
      data: {
        exception,
      },
    });
    Sentry.captureMessage('User Login failed', SeverityLevel_error);

    const errorMessage = exception?.errors || formatMessage({ id: 'alert.sign-in.login.failed' });
    mixpanel.track({
      name: 'sign-in : sign-in-failed',
      properties: { method: provider, errorMessage: errorMessage },
    });
    mixpanel.track({
      name: 'User logged in failed',
      properties: { method: provider, errorMessage: errorMessage },
    });
    dispatch(openAlert({ variant: 'error', message: errorMessage }));
    dispatch({
      type: userActionTypes.loginFailed,
    });
  }
};

export const refreshAuthToken = ({ callback }) => async (dispatch, getState) => {
  const refreshToken = getCookie('refresh_token');
  // in case there is other actions happen when fetching token
  if (callback) {
    refreshTokenCallbacks.push(callback);
  }
  const { userStore } = getState();
  if (userStore.refreshingToken) {
    return;
  }
  dispatch({
    type: userActionTypes.refreshingTokenStart,
  });
  try {
    const { data } = await postRefreshToken({ refreshToken });
    const { access_token, refresh_token, token_type, created_at, expires_in = 1800 } = data;
    dispatch({
      type: userActionTypes.refreshTokenSuccess,
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      createdAt: created_at,
      expires_in,
    });
    refreshTokenCallbacks.forEach((callbackFn) => callbackFn());
  } catch (e) {
    dispatch({
      type: userActionTypes.refreshTokenFailed,
    });
    // if refresh token failed then
    dispatch(logout());
  } finally {
    refreshTokenCallbacks.length = 0;
  }
};

export const updateUser = (user) => async (dispatch) => {
  dispatch({
    type: userActionTypes.updateUserStart,
  });
  try {
    await putUser(user);
    // update token after user update
    dispatch(
      refreshAuthToken({
        callback: () => {
          dispatch({
            type: userActionTypes.updateUserSuccess,
            user,
          });
        },
      })
    );
  } catch (e) {
    dispatch({
      type: userActionTypes.updateUserFailed,
    });
  }
};

export const logout = () => async (dispatch) => {
  intercom.logout();
  refreshTokenCallbacks.length = 0;
  try {
    await logoutUser();
    dispatch({
      type: userActionTypes.logout,
    });
  } catch (e) {
    dispatch({
      type: userActionTypes.logout,
    });
  }
};

class IntercomCredentialError extends Error {
  constructor(error) {
    super(error);
    this.name = 'IntercomCredentialError';
  }
}

export const getUser = () => async (dispatch) => {
  dispatch({
    type: userActionTypes.getUserStart,
  });
  try {
    const { data: user } = await callGetuser();
    Sentry.setUser({ id: user.id, email: user.email });
    mixpanel.updateUser(user);
    let hmac_digest;
    try {
      const { data: intercomData } = await getUserIntercomCredentials();
      hmac_digest = intercomData.data.attributes.hmac_digest;
    } catch (e) {
      Sentry.captureException(new IntercomCredentialError('failed to get intercom hmac_digest'));
    }
    const newUser = {
      ...user,
      hmac_digest,
    };
    intercom.boot(newUser);
    dispatch({
      type: userActionTypes.getUserSuccess,
      user: newUser,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    dispatch({ type: userActionTypes.getUserFailed });
    dispatch(logout());
  }
};

export function setNewUserSearchAddress(address) {
  return async function setUserSearchAddressAction(dispatch, getState, { intl }) {
    const { formatMessage } = intl(getState());
    dispatch({ type: userActionTypes.persistNewUserAddress_start });
    try {
      // TODO: Keep these service regions
      // eslint-disable-next-line no-unused-vars
      const { address: persistedAddress, serviceRegions } = await persistNewAddress(address);
      dispatch({
        type: userActionTypes.persistNewUserAddress_success,
        address: persistedAddress,
      });
      dispatch(updateUserServiceRegion(persistedAddress.serviceRegionIds[0]));
      const geoLocation = {
        lat: persistedAddress.latitude,
        lng: persistedAddress.longitude,
        country: persistedAddress.country,
      };
      dispatch({
        type: userActionTypes.updateUserSearchAddress,
        address: persistedAddress,
        geoLocation,
        serviceRegionId: persistedAddress.serviceRegionIds[0],
      });
      dispatch(deliveryActions.fetchDeliveryGroups());
    } catch (e) {
      dispatch({
        type: userActionTypes.persistNewUserAddress_failed,
      });
      dispatch(
        openAlert({
          variant: 'error',
          message: formatMessage({ id: 'actions.new_address.invalid_address' }),
        })
      );
    }
  };
}

export function chooseCurrentAddress(address, serviceRegionId) {
  return async function chooseCurrentAddressAction(dispatch) {
    const geoLocation = { lat: address.latitude, lng: address.longitude, country: address.country };
    dispatch(updateUserServiceRegion(serviceRegionId));
    dispatch({
      type: userActionTypes.selectUserAddress,
      addressId: address.id,
      address,
      geoLocation,
      serviceRegionId,
    });
    dispatch(deliveryActions.fetchDeliveryGroups());
  };
}

export const updateUserAddress = ({ address }) => async (dispatch, getState, { intl }) => {
  const state = getState();
  const { formatMessage } = intl(state);
  const updateAddress2Only = (address, originAddress) => {
    return (
      address.address_1 === originAddress.address_1 &&
      address.address_2 !== originAddress.address_2 &&
      address.city === originAddress.city &&
      address.state === originAddress.state &&
      address.country === originAddress.country
    );
  };
  dispatch({
    type: userActionTypes.updateUserAddressStart,
  });
  try {
    const {
      data: { address_id },
    } = await putAddress(address);
    dispatch({
      type: userActionTypes.updateUserAddressSuccess,
      address,
      newAddressId: address_id,
      updateAddress2Only: updateAddress2Only(
        address,
        state.userStore.user.addresses.find((addr) => addr.id === address.id)
      ),
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.addresses.update.success' }),
        variant: 'success',
      })
    );
  } catch (e) {
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.addresses.update.failed' }),
        variant: 'error',
      })
    );
    dispatch({
      type: userActionTypes.updateUserAddressFailed,
    });
  }
};

export const deleteUserAddress = ({ address }) => async (dispatch, getState, { intl }) => {
  const state = getState();
  const { formatMessage } = intl(state);
  dispatch({
    type: userActionTypes.deleteUserAddressStart,
  });
  try {
    await deleteAddress(address);
    dispatch({
      type: userActionTypes.deleteUserAddressSuccess,
      address,
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.addresses.delete.success' }),
        variant: 'success',
      })
    );
  } catch (e) {
    dispatch({
      type: userActionTypes.deleteUserAddressFailed,
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.addresses.delete.failed' }),
        variant: 'error',
      })
    );
  }
};

export const setUserAddressError = (error) => (dispatch) => {
  dispatch({
    type: userActionTypes.setUserAddressError,
    error,
  });
};

export const getCredit = () => async (dispatch) => {
  dispatch({
    type: userActionTypes.getCreditStart,
  });
  try {
    const { data } = await getCreditTransations();
    const { creditAdditions, creditDeductions } = snakeToCamel(data);
    dispatch({
      type: userActionTypes.getCreditSuccess,
      creditAdditions,
      creditDeductions,
    });
  } catch (e) {
    dispatch({
      type: userActionTypes.getCreditFailed,
    });
  }
};

export const getReferralCode = () => async (dispatch, getState) => {
  dispatch({
    type: userActionTypes.getReferralCodeStart,
  });
  try {
    /** @type {UserState.User} */
    const user = selectUser(getState());
    const locale = selectLocale(getState());
    const response = await callGetReferralCode(locale, user.id);
    const {
      data: {
        data: { attributes: referral_code },
      },
    } = response;
    dispatch({
      type: userActionTypes.getReferralCodeSuccess,
      referral_code,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    dispatch({
      type: userActionTypes.getReferralCodeFailed,
    });
  }
};

export const getMembershipSaving = () => async (dispatch) => {
  dispatch({
    type: userActionTypes.getMembershipSavingStart,
  });
  try {
    const { lifetime_saving_estimate } = await callGetMembershipSaving();
    dispatch({
      type: userActionTypes.getMembershipSavingSuccess,
      lifeTimeSaving: lifetime_saving_estimate,
    });
  } catch (e) {
    dispatch({
      type: userActionTypes.getMembershipSavingFailed,
    });
  }
};

export const setUserServiceType = ({ serviceType }) => (dispatch) => {
  dispatch({
    type: userActionTypes.setUserServiceType,
    serviceType,
  });
  dispatch(deliveryActions.fetchDeliveryGroups());
};

export const getUserNotification = ({ addressId, geoLocation }) => async (dispatch) => {
  dispatch({
    type: userActionTypes.getUserNotificationStart,
  });
  try {
    const { data } = await getDinerNotices({ addressId, geoLocation });
    dispatch({
      type: userActionTypes.getUserNotificationSuccess,
      notifications: data.data,
    });
  } catch (e) {
    dispatch({
      type: userActionTypes.getUserNotificationFailed,
    });
  }
};

export const sendCode = (phoneNumber) => async (dispatch, getState, { intl }) => {
  const state = getState();
  const { formatMessage } = intl(state);
  dispatch({
    type: userActionTypes.sendUserPhoneVerificationStart,
  });
  try {
    await postCode(phoneNumber);
    dispatch({
      type: userActionTypes.sendUserPhoneVerificationSuccess,
      phoneNumber,
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.verification.phone_number.code_send.success' }),
        variant: 'success',
      })
    );
  } catch (e) {
    const { response } = e;
    let message = formatMessage({ id: 'actions.user.verification.phone_number.code_send.failed' });
    if (response && response.data && response.data.errors) {
      message = response.data.errors;
    }
    dispatch({
      type: userActionTypes.sendUserPhoneVerificationFailed,
      phoneNumber,
      message,
    });
    dispatch(
      openAlert({
        message,
        variant: 'error',
      })
    );
    throw e;
  }
};

export const verifyCode = (code) => async (dispatch, getState, { intl }) => {
  const state = getState();
  const { formatMessage } = intl(state);
  dispatch({
    type: userActionTypes.verifyUserStart,
  });
  try {
    await putCode(code);

    dispatch(
      refreshAuthToken({
        callback: () => {
          dispatch({
            type: userActionTypes.verifyUserSuccess,
          });
          dispatch(
            openAlert({
              message: formatMessage({ id: 'actions.user.verification.phone_number.success' }),
              variant: 'success',
            })
          );
          dispatch(closeDialog());
        },
      })
    );
  } catch (e) {
    dispatch({
      type: userActionTypes.verifyUserFailed,
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.user.verification.phone_number.failed' }),
        variant: 'error',
      })
    );
  }
};

const updateUserServiceRegion = (serviceRegionId) => async (dispatch, getState) => {
  const state = getState();
  /** @type {UserState.User} */
  const user = selectUser(state);
  if (user) {
    if (String(user.service_region_id) !== String(serviceRegionId)) {
      await dispatch(
        updateUser({
          service_region_id: serviceRegionId || 1,
        })
      );
    }
  }
};
